<style lang="postcss">
	:global(.suffix-overwrite) {
		color: var(--theme-color);
		margin: 0 1px;
	}
	.prefix,
	.suffix {
		display: inline-flex;
		height: 100%;
		flex-flow: row nowrap;
		align-items: center;
		gap: 0.25rem;
		padding: 0.5rem 0.75rem;
		height: 100%;
		min-height: 2.45rem;
		font-weight: inherit;
	}
	.prefix {
		border-right-width: 0px;
		border-style: solid;
	}
	.suffix {
		border-left-width: 0px;
		border-style: solid;
	}
	.field-input {
		display: flex;
		flex-direction: row;
		align-items: stretch;
		justify-items: stretch;
		padding: 0;
	}
	.input-area {
		flex: 1 1 0%;
		min-width: 0;
	}
	.input-area input,
	.input-area select {
		width: 100%;
		outline: none;
		background: none;
		padding: 0.5rem 0.75rem;
	}
	.input-area select {
		appearance: none;
		background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' width='12' height='12' viewBox='0 0 24 24' stroke-width='2' stroke='currentColor'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' d='M19.5 8.25l-7.5 7.5-7.5-7.5'%3E%3C/path%3E%3C/svg%3E");
		background-repeat: no-repeat;
		background-position: right 0.7rem top 50%;
		background-size: 0.65rem auto;
	}
</style>

<script lang="ts">
	import { getContext } from "svelte";
	import { getImageSrc } from "$lib/helpers/functions";
	import { numericInput } from "$lib/actions/numericInput";
	import type { Writable } from "svelte/store";
	export let parentClass: string = "";
	export let name: string = "";
	export let label: string = "";
	export let type: "email" | "tel" | "text" | "select" = "text";
	export let value: string;
	export let element: HTMLInputElement | HTMLSelectElement =
		{} as HTMLInputElement;
	export let inputmode:
		| "email"
		| "tel"
		| "text"
		| "numeric"
		| "decimal"
		| "url"
		| "search" = "text";
	export let options: ChoiceOption[] = [];
	export let prefixIcon: Embed = null;
	export let suffixIcon: Embed = null;
	export let prefixText = "";
	export let suffixText = "";
	export let loading: boolean = false;
	export let suffixOverwrite: RemoteValidation["state"] | null = null;
	const suffixOverwriteValid = `<svg class='suffix-overwrite' xmlns="http://www.w3.org/2000/svg" width="19" height="19" viewBox="0 0 256 256"><polyline points="88 136 112 160 168 104" fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="18"/><circle cx="128" cy="128" r="96" fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="18"/></svg>`;
	const suffixOverwriteLoading = `<div class='loader'></div>`;
	const direction = getContext<Writable<string>>("direction");
	function getSuffix() {
		if (suffixOverwrite === null || suffixOverwrite === "invalid") {
			return suffixIcon.content;
		}
		if (suffixOverwrite === "valid") {
			return suffixOverwriteValid;
		}
		if (suffixOverwrite === "loading") {
			return suffixOverwriteLoading;
		}
	}
</script>

<div class="field-input {parentClass}">
	{#if prefixIcon || prefixText?.trim()}
		<button
			type="button"
			class="prefix"
			style="{$direction === 'ltr'
				? 'padding-right: 0rem;'
				: 'padding-left: 0rem;'}"
			title="{prefixText || 'field prefix icon'}"
			tabindex="-1"
			on:click="{(e) => {
				element.focus();
			}}">
			{#if prefixIcon}
				{#if prefixIcon.type === "svg"}
					{#key prefixIcon.content}
						<svg-load
							height="1.3rem"
							style="display: flex; height: 100%; padding: 0; min-height: 1.3rem"
							src="{getImageSrc(prefixIcon)}"></svg-load>
					{/key}
				{:else if prefixIcon.type === "emoji"}
					<span style="">{prefixIcon.content}</span>
				{:else if prefixIcon.type === "inline_svg"}
					{@html prefixIcon.content}
				{/if}
			{/if}
			{prefixText}
		</button>
	{/if}
	<div
		class="input-area"
		class:has-suffix="{suffixIcon || suffixText?.trim()}"
		class:has-prefix="{prefixIcon || prefixText?.trim()}">
		{#if type === "email"}
			<input
				type="email"
				{...$$restProps}
				on:input
				on:change
				use:numericInput="{inputmode === 'numeric' || inputmode === 'decimal'
					? { decimal: inputmode === 'decimal' }
					: { disabled: true }}"
				bind:this="{element}"
				name="{name}"
				inputmode="{inputmode}"
				aria-label="{label || ''}"
				class="{$$props.class || ''}"
				bind:value="{value}" />
		{:else if type === "tel"}
			<input
				type="tel"
				{...$$restProps}
				on:input
				on:change
				use:numericInput="{inputmode === 'numeric' || inputmode === 'decimal'
					? { decimal: inputmode === 'decimal' }
					: { disabled: true }}"
				bind:this="{element}"
				name="{name}"
				inputmode="{inputmode}"
				aria-label="{label || ''}"
				class="{$$props.class || ''}"
				bind:value="{value}" />
		{:else if type === "select"}
			<select
				{...$$restProps}
				on:input
				on:change
				bind:this="{element}"
				name="{name}"
				aria-label="{label || ''}"
				class="{$$props.class || ''}"
				bind:value="{value}">
				{#each options as option}
					<option value="{option.value}">{option.label}</option>
				{/each}
			</select>
		{:else}
			<input
				type="text"
				{...$$restProps}
				on:input
				on:change
				use:numericInput="{inputmode === 'numeric' || inputmode === 'decimal'
					? { decimal: inputmode === 'decimal' }
					: { disabled: true }}"
				bind:this="{element}"
				name="{name}"
				inputmode="{inputmode}"
				aria-label="{label || ''}"
				class="{$$props.class || ''}"
				bind:value="{value}" />
		{/if}
	</div>
	{#if (suffixIcon || suffixText?.trim()) && !loading}
		<button
			type="button"
			class="suffix"
			style="{$direction === 'ltr'
				? 'padding-left: 0rem;'
				: 'padding-right: 0rem;'}"
			title="{suffixText || 'field suffix icon'}"
			tabindex="-1"
			on:click="{(e) => {
				element.focus();
			}}">
			{#if suffixIcon}
				{#if suffixIcon.type === "svg"}
					{#key suffixIcon.content}
						<svg-load
							height="1.3rem"
							style="display: flex; height: 100%; padding: 0; min-height: 1.3rem"
							src="{getImageSrc(suffixIcon)}"></svg-load>
					{/key}
				{:else if suffixIcon.type === "emoji"}
					<span style="">{suffixIcon.content}</span>
				{:else if suffixIcon.type === "inline_svg"}
					{@html getSuffix()}
				{/if}
			{/if}
			{suffixText}
		</button>
	{/if}
	{#if loading}
		<div
			class="suffix"
			style="{$direction === 'ltr'
				? 'padding-left: 0rem;'
				: 'padding-right: 0rem;'}">
			<div class="loader" style="width: 15px; height: 15px; margin: 0 3px">
			</div>
		</div>
	{/if}
</div>
